import type {
  ConsentData,
  ConsentManagerConfig,
  ConsentManagerAPI,
  ConsentManagerEvents,
  ConsentEvent,
  Region,
} from './types';
import { ConsentStorage } from '@/storage/consent-storage';
import { RegionDetector } from './region-detector';
import { EventEmitter } from './event-emitter';

export class ConsentManager implements ConsentManagerAPI {
  private storage: ConsentStorage;
  private config: ConsentManagerConfig;
  private eventEmitter: EventEmitter;
  private regionDetector: RegionDetector;
  private currentConsent: ConsentData | null = null;
  private isInitialized = false;
  private sessionId: string;

  constructor(config: ConsentManagerConfig) {
    this.config = {
      ...this.getDefaultConfig(),
      ...config,
    };

    this.storage = new ConsentStorage(
      this.config.projectId,
      this.config.cookieExpiry
    );

    this.eventEmitter = new EventEmitter();
    this.regionDetector = new RegionDetector();
    this.sessionId = this.generateSessionId();

    this.initialize();
  }

  private getDefaultConfig(): Partial<ConsentManagerConfig> {
    return {
      position: 'bottom',
      theme: 'light',
      autoDetectLanguage: true,
      showRejectAll: true,
      showCustomize: true,
      showFloatingButton: true,
      cookieExpiry: 365,
      respectDNT: true,
      respectGPC: true,
      debugging: false,
      enableScriptInterceptor: false, // Disabled by default for compatibility
      categories: this.getDefaultCategories(),
      regionalSettings: this.getDefaultRegionalSettings(),
    };
  }

  private async initialize(): Promise<void> {
    try {
      this.log('Initializing ConsentManager...');

      const region = await this.regionDetector.detectRegion();
      this.currentConsent = this.storage.getConsent();

      if (this.currentConsent) {
        this.currentConsent.region = region;
        this.log('Found existing consent:', this.currentConsent);
        this.emit('consent_given', this.currentConsent);
      } else {
        this.log('No existing consent found');

        if (this.shouldShowBanner(region)) {
          this.log('Showing consent banner for region:', region);
          this.emit('banner_shown');
        }
      }

      this.isInitialized = true;
      this.emit('ready');
      this.log('ConsentManager initialized successfully');
    } catch (error) {
      console.error('ConsentManager: Initialization failed', error);
      this.emit('error', error);
    }
  }

  public getConsent(): ConsentData | null {
    return this.currentConsent;
  }

  public async updateConsent(consent: Partial<ConsentData>): Promise<void> {
    const updatedConsent: ConsentData = {
      ...this.getDefaultConsent(),
      ...this.currentConsent,
      ...consent,
      timestamp: Date.now(),
      version: '1.0.2',
    };

    this.currentConsent = updatedConsent;
    this.storage.saveConsent(updatedConsent);

    await this.logConsentEvent('consent_updated', updatedConsent);
    this.emit('consent_updated', updatedConsent);

    if (this.config.onConsentChange) {
      this.config.onConsentChange(updatedConsent);
    }

    this.log('Consent updated:', updatedConsent);
  }

  public async acceptAll(): Promise<void> {
    const consent: ConsentData = {
      essential: true,
      analytics: true,
      advertising: true,
      functional: true,
      personalization: true,
      timestamp: Date.now(),
      version: '1.0.2',
      region: await this.regionDetector.detectRegion(),
    };

    await this.updateConsent(consent);
    this.emit('consent_given', consent);
    this.emit('banner_hidden');

    this.log('All consent categories accepted');
  }

  public async rejectAll(): Promise<void> {
    const region = await this.regionDetector.detectRegion();

    // COMPLIANCE FIX: "Reject All" should ALWAYS deny non-essential consent
    // regardless of region to ensure GDPR compliance
    const consent: ConsentData = {
      essential: true,
      analytics: false,        // Always false on reject
      advertising: false,      // Always false on reject
      functional: false,       // Always false on reject
      personalization: false,  // Always false on reject
      timestamp: Date.now(),
      version: '1.0.2',
      region,
    };

    await this.updateConsent(consent);
    this.emit('consent_given', consent);
    this.emit('banner_hidden');

    this.log('All non-essential consent categories rejected (GDPR compliant)');
  }

  public async revokeConsent(): Promise<void> {
    this.storage.clearConsent();
    this.currentConsent = null;

    await this.logConsentEvent('consent_withdrawn');
    this.emit('consent_withdrawn');
    this.emit('banner_shown');

    this.log('Consent revoked');
  }

  public showBanner(): void {
    this.emit('banner_shown');
    if (this.config.onBannerShow) {
      this.config.onBannerShow();
    }
  }

  public hideBanner(): void {
    this.emit('banner_hidden');
    if (this.config.onBannerHide) {
      this.config.onBannerHide();
    }
  }

  public showModal(): void {
    this.emit('modal_shown');
  }

  public hideModal(): void {
    this.emit('modal_hidden');
  }

  public onConsentChange(callback: (consent: ConsentData) => void): () => void {
    return this.addEventListener('consent_updated', callback);
  }

  public addEventListener(
    event: ConsentManagerEvents,
    callback: Function
  ): () => void {
    return this.eventEmitter.on(event, callback);
  }

  public getVersion(): string {
    return '1.0.2';
  }

  public getProjectId(): string {
    return this.config.projectId;
  }

  public isReady(): boolean {
    return this.isInitialized;
  }

  public emit(event: ConsentManagerEvents, data?: any): void {
    this.eventEmitter.emit(event, data);
  }

  private shouldShowBanner(region: Region): boolean {
    if (this.respectsDoNotTrack()) {
      return false;
    }

    const regionalSettings = this.config.regionalSettings?.[region];
    return regionalSettings?.showBanner ?? true;
  }

  private respectsDoNotTrack(): boolean {
    if (!this.config.respectDNT) return false;

    return (
      navigator.doNotTrack === '1' ||
      (navigator as any).msDoNotTrack === '1' ||
      (window as any).doNotTrack === '1'
    );
  }

  private getDefaultConsent(): ConsentData {
    return {
      essential: true,
      analytics: false,
      advertising: false,
      functional: false,
      personalization: false,
      timestamp: Date.now(),
      version: '1.0.2',
    };
  }

  private getDefaultCategories() {
    return [
      {
        id: 'essential' as const,
        name: 'Essential',
        description: 'Required for basic site functionality',
        required: true,
        purposes: ['Authentication', 'Security', 'Load balancing'],
      },
      {
        id: 'analytics' as const,
        name: 'Analytics',
        description: 'Help us understand how visitors use our site',
        required: false,
        purposes: ['Usage analytics', 'Performance monitoring'],
      },
      {
        id: 'advertising' as const,
        name: 'Advertising',
        description: 'Used to deliver relevant advertisements',
        required: false,
        purposes: ['Targeted advertising', 'Ad measurement'],
      },
      {
        id: 'functional' as const,
        name: 'Functional',
        description: 'Enable enhanced functionality and features',
        required: false,
        purposes: ['Chat widgets', 'Social media integration'],
      },
      {
        id: 'personalization' as const,
        name: 'Personalization',
        description: 'Remember your preferences and settings',
        required: false,
        purposes: ['User preferences', 'Customized content'],
      },
    ];
  }

  private getDefaultRegionalSettings() {
    return {
      EU: {
        defaultConsent: {
          analytics: false,
          advertising: false,
          functional: false,
          personalization: false,
        },
        showBanner: true,
        requireOptIn: true,
      },
      UK: {
        defaultConsent: {
          analytics: false,
          advertising: false,
          functional: false,
          personalization: false,
        },
        showBanner: true,
        requireOptIn: true,
      },
      US: {
        defaultConsent: {
          analytics: true,
          advertising: true,
          functional: true,
          personalization: true,
        },
        showBanner: true,
        requireOptIn: false,
        showDoNotSell: true,
      },
      CA: {
        defaultConsent: {
          analytics: false,
          advertising: false,
          functional: false,
          personalization: false,
        },
        showBanner: true,
        requireOptIn: true,
      },
    };
  }

  private async logConsentEvent(
    type: ConsentEvent['type'],
    consent?: ConsentData | undefined
  ): Promise<void> {
    if (!this.config.apiEndpoint) return;

    const event: ConsentEvent = {
      type,
      timestamp: Date.now(),
      version: '1.0.2',
      projectId: this.config.projectId,
      sessionId: this.sessionId,
      consent: consent || undefined,
      userAgent: navigator.userAgent,
    };

    try {
      await fetch(`${this.config.apiEndpoint}/consent/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      });
    } catch (error) {
      this.log('Failed to log consent event:', error);
    }
  }

  private generateSessionId(): string {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  private log(message: string, ...args: any[]): void {
    if (this.config.debugging) {
      console.log(`[ConsentManager] ${message}`, ...args);
    }
  }
}
